import React, { useContext, useEffect, useState } from "react"
import { Div, Text } from "atomize"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import TextInput from "../inputs/TextInput"
import ConfirmButtons from "../buttons/ConfirmButtons"
import { theme } from "../settings/config"
import { callFunction } from "../services/firebase.service"
import { schoolContext } from "../providers/school.provider"

const DELETE_WORD = "ELIMINA"

// Prima di chiudere un circolo si dice cosa ci va dietro, contato: "43
// allievi e 512 lezioni" pesa quanto deve pesare, mentre "sei sicuro?" non
// pesa niente. Un circolo aperto per sbaglio e mai usato si chiude senza
// cerimonie — la parola la si chiede solo quando c'è qualcosa da perdere.
export function DeleteSchoolModal({ closeModal }) {
	const { selectedSchool } = useContext(schoolContext)

	const [summary, setSummary] = useState(null)
	const [confirmation, setConfirmation] = useState("")
	const [isDeleting, setIsDeleting] = useState(false)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		const summarize = callFunction("summarizeSchool")
		summarize({ schoolID: selectedSchool })
			.then(({ data }) => setSummary(data))
			.catch(() => setFailed(true))
	}, [selectedSchool])

	const isEmpty = summary?.players === 0 && summary?.lessons === 0
	const canDelete = summary && (isEmpty || confirmation.trim() === DELETE_WORD)

	const handleDelete = async () => {
		setIsDeleting(true)
		try {
			const deleteSchool = callFunction("deleteSchool")
			await deleteSchool({ schoolID: selectedSchool, confirm: DELETE_WORD })

			// la lista dei circoli si rifà da sola: il documento del maestro
			// cambia, e il provider lo sta ascoltando
			toast.info(`${summary.schoolName} è stato chiuso`, { position: "top-center" })
			closeModal()
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a chiudere il circolo", {
				position: "top-center",
			})
			setIsDeleting(false)
		}
	}

	return (
		<Container>
			<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
				Chiudere {summary?.schoolName ?? "questo circolo"}?
			</Div>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				Non si torna indietro. Se vuoi tenerne una copia, scarica prima i tuoi dati dal
				profilo.
			</Text>

			{failed && (
				<Text textColor={theme.colors.red} m={{ b: "1rem" }}>
					Non riesco a leggere cosa c'è dentro: meglio non cancellare alla cieca.
					Riprova più tardi.
				</Text>
			)}

			{!summary && !failed && (
				<Text textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					Guardo cosa c'è dentro...
				</Text>
			)}

			{summary && (
				<>
					<Text textSize="body" m={{ b: "1rem" }}>
						{isEmpty
							? "Non c'è niente dentro: lo chiudi e basta."
							: `Spariscono ${summary.players} ${
									summary.players === 1 ? "allievo" : "allievi"
								} e ${summary.lessons} ${
									summary.lessons === 1 ? "lezione" : "lezioni"
								}, con i pagamenti e gli abbonamenti che ci sono attaccati.`}
					</Text>

					{summary.remaining === 0 && (
						<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
							È l'unico circolo che hai: dopo potrai crearne un altro o entrare in
							uno con un codice.
						</Text>
					)}

					{!isEmpty && (
						<Div m={{ b: "1rem" }}>
							<Text textSize="caption" textColor={theme.colors.text_light}>
								Scrivi {DELETE_WORD} per confermare
							</Text>
							<TextInput
								value={confirmation}
								textTransform="uppercase"
								handleChange={e => setConfirmation(e.target.value)}
							/>
						</Div>
					)}
				</>
			)}

			<ConfirmButtons
				confirmText="Chiudi il circolo"
				busy={isDeleting}
				busyText="Sto chiudendo..."
				disabled={!canDelete}
				onConfirm={handleDelete}
				cancelText="No, lo tengo"
				onCancel={closeModal}
			/>
		</Container>
	)
}

export default DeleteSchoolModal
